/**
 * Rich, detailed module summaries for the "Read the module" tab.
 * Keyed by module slug. Missing entries fall back to a generic auto-summary.
 * Each summary is a list of sections; each section has an h and body (paragraphs).
 */

export const MODULE_SUMMARIES = {
  "company-legal-setup": {
    kicker: "Module 1 · Reading time ~ 12 minutes",
    intro:
      "Legal setup isn't paperwork — it's the load-bearing foundation of every scalable CPG business. This module walks through the five decisions every founder must make before they burn a dollar on branding or inventory.",
    sections: [
      {
        h: "1 · Choose the right business entity",
        body: [
          "LLCs are simple, cheap, and give you liability protection — a great fit for a solo founder or a small partnership that isn't planning to raise venture capital in the next 18 months.",
          "Delaware C-Corporations are the standard for anything that wants outside capital. VCs and most sophisticated angels invest in Delaware C-Corps because they support preferred stock, multiple share classes, standardized governance, and predictable exit mechanics. If your plan involves an institutional round, start there — converting an LLC to a C-Corp later is painful and expensive.",
          "Real-world examples covered in the deck: Liquid Death, Prime Energy, RXBAR, Celsius — all Delaware C-Corps.",
        ],
      },
      {
        h: "2 · Register the business and get your EIN",
        body: [
          "Register with your state of formation, then apply for an EIN (Employer Identification Number) from the IRS. The EIN is your business's federal tax ID — you need it to open a business bank account, hire employees, and file taxes.",
          "Open a dedicated business bank account and — critically — never mix personal and business spending. Piercing the corporate veil is a real risk when finances are commingled, and it makes accounting, due diligence, and fundraising much harder.",
        ],
      },
      {
        h: "3 · Founder & shareholder agreements",
        body: [
          "Every co-founded business needs an operating agreement (for LLCs) or shareholders agreement (for C-Corps). Document ownership percentages, vesting schedules, voting rights, decision-making authority, roles, and what happens if a founder leaves.",
          "The most common founder catastrophe: 'we agreed 50/50 verbally' — and then, six months in, one founder disengages while still owning half the company. Written agreements prevent this.",
          "Include equity vesting (typically 4 years with a 1-year cliff). This protects both the company and the remaining founders if a co-founder walks.",
        ],
      },
      {
        h: "4 · Trademark & IP strategy",
        body: [
          "Trademark clearance before you invest in branding is non-negotiable. A basic trademark search costs a few hundred dollars; a forced rebrand after your packaging is printed costs tens of thousands, plus lost marketing momentum.",
          "Trademark your brand name, logo, and any distinctive slogans. File in the correct classes (Class 32 for non-alcoholic beverages, Class 30 for coffee/tea, etc.). If you plan international expansion, consider filing in target markets early — international trademarks operate on a first-to-file basis in most countries.",
          "Packaging IP: distinctive trade dress (unique packaging shapes, color combinations, label layouts) can also be protected. Liquid Death's tallboy positioning is a textbook example of protectable trade dress.",
        ],
      },
      {
        h: "5 · Insurance & regulatory compliance",
        body: [
          "General liability insurance is table stakes. Product liability insurance is required by nearly every major retailer — many will ask for a Certificate of Insurance (COI) naming them as an additional insured before they'll list your product.",
          "Regulatory compliance is category-specific. FDA covers most food and beverage. TTB regulates alcohol. California's Prop 65 requires specific warnings if certain chemicals are present. Get category-specific counsel — non-compliance can trigger recalls, fines, and retailer removal.",
        ],
      },
      {
        h: "Action checklist",
        body: [
          "Decide entity type and file formation documents.",
          "Get an EIN and open a business bank account.",
          "Draft and sign founder/operating/shareholder agreements with vesting.",
          "Run a trademark clearance search and file trademarks in the right classes.",
          "Bind general liability and product liability insurance policies.",
          "Confirm category-specific compliance (FDA / TTB / Prop 65 as applicable).",
        ],
      },
    ],
  },

  "product-development": {
    kicker: "Module 2 · Reading time ~ 14 minutes",
    intro:
      "Product development in CPG is a balancing act — taste, functionality, cost, manufacturability, shelf life, and regulatory viability all have to work together. The founders who scale are the ones who treat formulation as a commercial exercise, not a passion project.",
    sections: [
      {
        h: "The real objective: repeat purchases",
        body: [
          "The single most important product-development metric isn't taste-test scores or influencer buzz — it's whether consumers spend their own money on the product a second time.",
          "Everything upstream (formulation, packaging, positioning) exists to serve that one outcome. If you're not designing for repeat behavior, you're building a hobby.",
        ],
      },
      {
        h: "Balancing the six formulation constraints",
        body: [
          "Every formulation choice hits six variables simultaneously: taste, functionality, cost, manufacturability at scale, shelf life, and regulatory compliance. An impressive-sounding superfood ingredient can wreck three of the other five — that's why so many prototype-stage products never make it to consistent commercial production.",
          "Rule of thumb: an ingredient earns its place in the formula only if it improves consumer value AND is manufacturable at scale AND doesn't destroy your gross margin.",
        ],
      },
      {
        h: "Working with formulators and co-manufacturers",
        body: [
          "Experienced formulation partners are worth their weight in gold. They bridge founder vision with manufacturing reality — flagging cost-prohibitive ingredients, stability risks, and regulatory landmines before you invest in a full production run.",
          "Smaller co-packers usually beat larger ones for startups: lower MOQs, more flexibility on iteration, and easier communication. Larger co-packers offer scale but demand volume you probably don't need yet.",
          "Vet co-packers on quality systems (SQF, BRC), historical defect rate, lead time, and reference calls from other brands — not just price.",
        ],
      },
      {
        h: "Stability & shelf-life testing",
        body: [
          "Stability testing is where product dreams meet chemistry. A beverage that tastes perfect in fresh samples can separate, discolor, or develop off-flavors after 12 weeks. If you scale before you validate stability, thousands of units come back — or worse, sit on shelves and turn.",
          "Run accelerated stability studies (elevated temperature/humidity) alongside real-time studies. Accelerated data isn't a substitute for real-time — it's a leading indicator.",
        ],
      },
      {
        h: "Production run planning: start small",
        body: [
          "The single most expensive mistake in early CPG: doing a huge first production run because the co-packer offered a discount at 100,000 units. That discount evaporates the moment you discover a quality issue, a labeling error, or that consumers aren't repeating.",
          "The disciplined path: small validation run → measure repeat purchase + retailer sell-through → refine → scale.",
        ],
      },
      {
        h: "Feedback loops beat perfectionism",
        body: [
          "The market is the only reliable judge. Founders who chase 'perfect' before launching almost always ship late, with a product tuned to their own taste rather than the consumer's. Ship the smallest viable version, listen, and iterate.",
        ],
      },
    ],
  },

  "brand-strategy": {
    kicker: "Module 3 · Reading time ~ 13 minutes",
    intro:
      "In a category with 40 similar products on the shelf, branding is the only thing that lets consumers pick you deliberately. This module explains what a brand actually is (a place in the consumer's mind), why emotion beats spec, and how to make branding decisions using data instead of ego.",
    sections: [
      {
        h: "Consumers buy emotionally — then justify rationally",
        body: [
          "Very few CPG purchases are purely rational. Consumers pick up the product that feels right, then they rationalize the choice afterwards ('the ingredients are cleaner', 'it's from a small brand', 'my friend recommended it').",
          "This is why two beverages with nearly identical ingredient decks can have wildly different sales trajectories — the winner isn't better formulated, it's better positioned.",
        ],
      },
      {
        h: "What positioning actually is",
        body: [
          "Positioning is the unique place your brand occupies in the consumer's mind relative to competitors. It's who you're for, what you promise, and why you're the answer.",
          "Weak positioning: 'Contains 100mg of caffeine, 12oz aluminum can.' That's a spec sheet. Strong positioning: 'Helping ambitious professionals stay focused without the crash.' That's a promise anchored to an identity.",
        ],
      },
      {
        h: "The 'for everyone' trap",
        body: [
          "If a founder says 'our product is for everybody', the positioning work isn't done yet. A brand for everyone is memorable to no one. Pick a wedge, own it, then expand from strength.",
        ],
      },
      {
        h: "Consistency beats novelty",
        body: [
          "The strongest brands deliver the same experience across every touchpoint — packaging, website, retail activation, DTC, social, customer service. That consistency is the thing that builds trust, and trust is what drives repeat purchase and eventually category leadership.",
          "Founders who redesign every six months confuse the market and never build cumulative brand equity.",
        ],
      },
      {
        h: "Listen to what the market is buying",
        body: [
          "Positioning is a hypothesis until real purchase behavior confirms it. If you positioned a wellness drink for busy professionals and endurance athletes are the ones actually buying, the market is telling you something. Don't fight it — evolve the positioning without abandoning the core brand promise.",
        ],
      },
      {
        h: "The moat that can't be copied",
        body: [
          "Competitors can copy your ingredients, your packaging dimensions, and even your color palette. The one thing they can't copy is the emotional connection you've built with consumers. That's the long-term moat — and it compounds.",
        ],
      },
    ],
  },
};
